// The guided tour of the Bid Tracker, rewritten for the CRM redesign of
// 29 September 2026: Pipeline, My bids, Bids, Review and Reports, rows that
// carry the fields each person chooses, and the bid record with its stepper,
// stage line and tabs. Brought up to date with the streamlining of 25 October
// 2026: Triage, Waiting on, Done on the next action, Buyers' People view.
// Leads (migration 0417, 29 October 2026) added the Leads tab, New lead and
// Convert to tender.
//
// Steps point at [data-tour] anchors on BidTrackerPage. Every step carries a
// `before` that clicks the tab it lives on, so stepping backwards leaves the
// page as it was going forwards. The engine (shared/FeatureTour.jsx) and the
// show-once record (hooks/useFeatureTour.js) are generic; only this file knows
// about bids.

export const BID_TRACKER_TOUR_ID = 'bid-tracker';

const clickTab = (selector) => {
  const btn = document.querySelector(selector);
  if (btn && btn.getAttribute('aria-pressed') !== 'true') btn.click();
};
const onPipeline = () => clickTab('[data-tour="tab-pipeline"]');
const onMine = () => clickTab('[data-tour="tab-mine"]');
const onBids = () => clickTab('[data-tour="tab-bids"]');
const onLeads = () => clickTab('[data-tour="tab-leads"]');
const onReview = () => clickTab('[data-tour="tab-review"]');
const onTriage = () => clickTab('[data-tour="tab-triage"]');
const onBuyers = () => { clickTab('[data-tour="tab-buyers"]'); clickTab('[data-tour="buyers-views"] button:first-child'); };
const onPeople = () => { clickTab('[data-tour="tab-buyers"]'); setTimeout(() => clickTab('[data-tour="buyers-people"]'), 0); };
const onReport = () => clickTab('[data-tour="tab-report"]');

export const bidTrackerTourSteps = [
  {
    title: 'The Pipeline Tracker',
    target: '[data-tour="page-header"]',
    before: onPipeline,
    body: [
      'Every tender, framework and quote we are chasing lives here, and so does every lead that is not a formal tender yet, in eight tabs: Pipeline, Triage, My bids, Bids, Leads, Review, Buyers and Reports. Hide values, by New lead and New bid, masks every figure when you are sharing your screen.',
      'Use Next (or the arrow keys) to walk through, or Skip if you would rather explore. Either way it will not pop up again; the ? by the title replays it.',
    ],
  },
  {
    title: 'The pipeline at a glance',
    target: '[data-tour="pipeline-kpis"]',
    before: onPipeline,
    body: [
      'Five tiles: open bids, the ones that need you, what is due within 14 days, what is waiting on a buyer (and how many have gone quiet), and the open pipeline ex VAT. Framework ceilings are counted, never added to a total.',
      'Each tile opens the bids behind its number.',
    ],
  },
  {
    title: 'Waiting on',
    target: '[data-tour="waiting-on"]',
    before: onPipeline,
    body: [
      'One chip per person with a next action on them, busiest first, with any overdue counted in red. A next action is on the bid owner unless someone else is named on it.',
      'Pick a person to show only the bids waiting on them; Show everyone puts the board back.',
    ],
  },
  {
    title: 'The board',
    target: '[data-tour="board"]',
    before: onPipeline,
    body: [
      'A column per stage, from Upcoming to Won, each with its count and its value ex VAT. On a narrow screen the columns wrap into rows rather than scrolling sideways.',
      'A card shows the reference, how urgent it is (a red or orange edge when the deadline is close), the value, any key date and the next action with the initials of the person it is on. It reads Next: none set in red when nothing is recorded. The ⋯ on a card moves it to another stage; if the move is refused, the card says why. Lost, No-bid and Parked sit under the board, or on it if you tick Include outcome columns.',
    ],
  },
  {
    title: 'Triage',
    target: '[data-tour="triage"]',
    before: onTriage,
    body: [
      'New notices land here at Upcoming, soonest deadline first. Decide each one: Evaluate starts the bid, No-bid closes it with a reason, Park takes it off the pipeline for now.',
      'Tick several to close them together with one reason. What happened is said in the bar at the top, and anything that could not be closed is named there with the reason.',
    ],
  },
  {
    title: 'What needs you this morning',
    target: '[data-tour="attention-cards"]',
    before: onMine,
    body: [
      'My bids opens on cards: deadlines within 3 days and within 14, open bids with no next action, bids waiting on a buyer that are due a chase, portals not checked for a week while a response is being written, and any bid submitted six months ago with no decision. The same rules send the morning email and count the badge by Pipeline Tracker in the menu, so they never disagree.',
      'A bid appears here because of something you can do about it. Set a next action, log the chase, check the portal or close the long wait, and it drops off. Bids owned by someone else with a next action on you are listed below your own.',
    ],
  },
  {
    title: 'Raising a bid',
    target: '[data-tour="new-bid"]',
    before: onMine,
    body: [
      'New bid asks for the opportunity, the client (pick one, or add a new client without leaving the form), an owner and a deadline. A bid spotted on a pipeline notice, with no deadline yet, can be raised with a watch date instead.',
      'Each field has one job. The summary is the standing picture in a line or two; the next action is one step, with a date and the person it is on; a key date (a site visit, clarifications closing) is a date with a label. Everything that happens goes on the bid\'s timeline.',
    ],
  },
  {
    title: 'Bids: everything, filtered',
    target: '[data-tour="quick-filters"]',
    before: onBids,
    body: [
      'Bids is the whole tracker as rows: the reference and title, the buyer, sector, owner and when the portal was last checked, the next action, then the value, deadline and pills. Filter with the chips (Upcoming, Awaiting decision, No next action, Decided and more), the status and sector lists, or by reference, title or buyer.',
      'Awaiting decision is the state for a submitted bid waiting on the buyer: it stops reading as overdue against its own deadline, and asks for a chase once a month.',
      'Fields, beside the filters, chooses what shows under each row: service line, the contact, the key date, who the next action is on, and any field added for everyone. Your fields, filters and settings save against your login, so the tracker looks the same on every machine.',
    ],
  },
  {
    title: 'Leads',
    target: '[data-tour="lead-kpis"]',
    before: onLeads,
    body: [
      'Leads holds the conversations that are not formal tenders: a referral, an enquiry, an MSP renewal. Raise one with New lead. A lead has an owner, a source, a next action with a due date (its clock, in place of a deadline), a timeline and people, but no portal, deadline or compliance matrix.',
      'Leads are kept out of the Pipeline tiles, Triage, Bids and Reports, so the tender figures stay clean; their value is shown here on its own. Follow-up due lists the ones whose next action is overdue, due today or missing, and the same goes to the owner in the morning email. When a formal ITT arrives, Convert to tender on the lead\'s More menu moves it onto the tender pipeline with its history.',
    ],
  },
  {
    title: 'Review',
    target: '[data-tour="review-attention"]',
    before: onReview,
    body: [
      'Review is the week in one place, bids and leads together: what needs attention and why, what changed status this week, and the bids whose portal was checked with nothing new. It opens on your own bids; Everyone shows the whole team.',
      'Your actions on bids are listed by the day they are due. They are the same actions as Comments and actions on each bid\'s timeline: tick one, say what was done, and it is complete in both places.',
    ],
  },
  {
    title: 'Buyers',
    target: '[data-tour="buyers-list"]',
    before: onBuyers,
    body: [
      'Buyers has two views, Organisations and People. Organisations lists every organisation we have bid to, with its sector, how many bids, their value and our won and lost record. Pick one for its organisation number, the portal it tenders on, how it usually buys, an intelligence note the whole team can add to, its bids and its contacts.',
      'A buyer marked unlinked is a name typed on bids with no client behind it. Link those bids to their client from the bid\'s Edit form.',
    ],
  },
  {
    title: 'The people at our buyers',
    target: '[data-tour="contacts-grid"]',
    before: onPeople,
    body: [
      'People, on Buyers, lists the people at our buyers, from the same contacts list as CRM Contacts, where they are added and edited. Each card shows their roles on bids (procurement, referee, portal contact, decision maker), how to reach them, when we last spoke and the bids they are on.',
      'When one referee has been asked on three or more bids in a year, a warning below the cards suggests asking someone else.',
    ],
  },
  {
    title: 'Inside a bid',
    target: '[data-tour="board"]',
    before: onPipeline,
    body: [
      'Open a bid for its record. The header carries Checked portal today, Record outcome, Edit and More (merge a duplicate, and on a lead, Convert to tender). A lead\'s record is the same without the portal, the deadline and the compliance matrix, and its stepper runs Upcoming, Evaluating, In Discussion, Won. The stepper runs from Upcoming to Won; click a stage to move the bid there, and a refusal is shown just beneath it.',
      'The navy stage line holds the standing summary and the next action, which stays pinned at the top as you scroll. When the next action is done, press Done: say what was done and what comes next, and both are saved in one go, the first on the timeline and the second on the stage line.',
      'The Timeline tab takes calls, emails, portal messages, site visits, clarifications, chases and notes, merged with status changes and comments; + update the next step moves the stage line from the same entry. Comments and actions sit under the composer. Documents, Files (the bid\'s SharePoint folder, opened in Ops), the compliance matrix and Hardware are tabs beside it, and so is Surveys for those who hold the Surveys page: a site survey requested before the win shows on the opportunity the win raises.',
      'The compliance matrix lists every ITT question with its word count and attachments, ticked or waived before submission; Responded is refused while any row is still to do. Name a reviewer and a review date there, and point each row at the Content Library item that answered it.',
      'Hardware holds the supplier quotes and vendor deals for any kit in the bid, each with its lines, cost and sell. Recording the win carries those lines onto the Opportunity Tracker as a Draft opportunity; Carry to tracker on the tab picks up anything added after the win.',
    ],
  },
  {
    title: 'The Content Library',
    target: '[data-tour="page-header"]',
    before: onPipeline,
    body: [
      'Content Library, next to Pipeline Tracker in the menu, holds approved answers, references, case studies and policies, tagged, each with an owner and a review date; accreditations come from the Accreditations tab of QMS Control. Items past their review date reach their owner in the morning email.',
    ],
  },
  {
    title: 'Reports',
    target: '[data-tour="report-controls"]',
    before: onReport,
    body: [
      'Reports covers a period: bids submitted, outcomes, what is still in flight, values ex VAT, activity by owner, win rate by sector and service line, and the loss root causes. Export to Excel or print the summary for the monthly report.',
    ],
  },
];
